import {
  Avatar,
  Box,
  Typography,
  useTheme,
  Skeleton,
  Badge,
  styled,
} from '../../mui';
import { TextButton } from '../Button';

type ProfileBadgeProps = {
  name?: string;
  subtitle?: string;
  picture?: string;
  onClick: () => void;
  isLoading?: boolean;
  notificationsCount?: number;
};

const ProfileBadge = ({
  name,
  subtitle,
  picture,
  onClick,
  isLoading = false,
  notificationsCount = 0,
}: ProfileBadgeProps) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      top: 2,
      marginRight: 18,
    },
  }));
  return (
    <Box
      sx={{
        display: 'flex',
        position: 'absolute',
        bottom: 0,
        width: '100%',
        backgroundColor: palette.background.paper,
        pt: 1,
      }}
      data-tour='nav-profile'
    >
      <StyledBadge
        badgeContent={notificationsCount}
        color='secondary'
        sx={{ width: '100%' }}
      >
        <TextButton
          onClick={(evt) => {
            evt.preventDefault();
            onClick();
          }}
          title={subtitle ? `${name}\n${subtitle}` : name}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            mx: 2,
            padding: 1,
            borderRadius: 2,
            border: `1px solid ${palette.primary.main}`,
            color: palette.primary.main,
          }}
          startIcon={
            <Avatar
              src={picture}
              sx={{
                backgroundColor: picture
                  ? palette.background.paper
                  : palette.primary.main,
                border: picture ? '1px solid' : 'none',
                borderColor: 'divider',
                '& .MuiAvatar-img': { objectFit: 'cover' },
                '& .MuiSvgIcon-root': {
                  fill: palette.background.paper,
                },
              }}
            ></Avatar>
          }
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            {isLoading ? (
              <>
                <Skeleton variant='text' width='80%' height={20} />
                <Skeleton variant='text' width='60%' height={16} />
              </>
            ) : (
              <>
                <Typography
                  variant='badgeLabel'
                  sx={{
                    fontSize: { xs: '14px', md: '12px' },
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  {name}
                </Typography>
                {subtitle && (
                  <Typography
                    variant='caption'
                    sx={{
                      fontSize: { xs: '12px', md: '10px' },
                      opacity: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </TextButton>
      </StyledBadge>
    </Box>
  );
};

export { ProfileBadge };
